import React from 'react';
import {
  Image,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  TouchableOpacity,
  View,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';
import Animated, {
  Easing,
  FadeIn,
  FadeInDown,
  FadeInLeft,
  useAnimatedStyle,
  useSharedValue,
  withSpring,
  withTiming,
} from 'react-native-reanimated';

import { Text } from '@/components/ui/text';
import { Sheet } from '@/components/ui/Sheet';
import ErrorBoundary from '../../components/ErrorBoundary';
import { reviewDesk } from '../theme';
import { ROUTES } from '../../navigation/routes';
import { MCS_ENABLED } from '../../config/featureFlags';
import { useTranslation } from '../../hooks/useTranslation';
import { useTabAccess } from '../../hooks/useTabAccess';
import { useResponsive } from '../../hooks/useResponsive';
import { splitPhoneNav } from './shellNav';
import {
  isFeedbackAvailable,
  openFeedback,
  subscribeFeedbackAvailability,
  usesHeaderFeedbackEntry,
} from '../../components/feedback/feedbackLauncher';
import { useMentorTrainingAccess } from '../../hooks/useMentorTrainingAccess';
import { HelpMenu } from '../../components/tutorial/HelpMenu';
import { transcriptionAPI, userDocumentsAPI, type UserDocumentItem } from '../../services/api';

const NAV_SPRING = { damping: 18, stiffness: 220, mass: 0.9 };
const NAV_PRESS_SPRING = { damping: 14, stiffness: 320, mass: 0.7 };
const NAV_FADE_TIMING = { duration: 160, easing: Easing.out(Easing.cubic) };

const AppLogo = require('../../../assets/icon-web.png');

type ShellActive =
  | 'dashboard'
  | 'chat'
  | 'sessions'
  | 'progress'
  | 'profile'
  | 'students'
  | 'documents'
  | 'mentor-training'
  | 'mentor-engagements'
  | 'mcs-preparation'
  | 'school-management'
  | 'platform-admin';

type ShellNavItem = {
  key: ShellActive;
  icon: keyof typeof MaterialIcons.glyphMap;
  route: string;
  labelKey: string;
  fallback: string;
};

export interface WebAppShellRecentItem {
  id: string | number;
  title: string;
  subtitle?: string;
  active?: boolean;
  onPress?: () => void;
}

interface WebAppShellProps {
  active: ShellActive;
  children: React.ReactNode;
  topRight?: React.ReactNode;
  recentItems?: WebAppShellRecentItem[];
  contentMaxWidth?: number | string;
  scroll?: boolean;
}

const baseNavItems: ShellNavItem[] = [
  { key: 'dashboard', icon: 'home', route: ROUTES.TABS_DASHBOARD, labelKey: 'navigation.home', fallback: 'Ana sayfa' },
  { key: 'chat', icon: 'chat-bubble-outline', route: ROUTES.TABS_CHAT, labelKey: 'navigation.chat', fallback: 'Sohbet' },
  { key: 'sessions', icon: 'folder-open', route: ROUTES.TABS_LIBRARY, labelKey: 'navigation.sessions', fallback: 'Oturumlar' },
  { key: 'progress', icon: 'bar-chart', route: ROUTES.TABS_PROGRESS, labelKey: 'navigation.progress', fallback: 'İlerleme' },
  { key: 'profile', icon: 'person-outline', route: ROUTES.PROFILE, labelKey: 'navigation.profile', fallback: 'Profil' },
];

const mentorTrainingNavItem: ShellNavItem = {
  key: 'mentor-training',
  icon: 'school',
  route: ROUTES.TABS_MENTOR_TRAINING,
  labelKey: 'navigation.mentorTraining',
  fallback: 'Mentor Koç Eğitimi',
};

const mentorEngagementsNavItem: ShellNavItem = {
  key: 'mentor-engagements',
  icon: 'assignment-turned-in',
  route: ROUTES.MENTOR_ENGAGEMENTS,
  labelKey: 'navigation.mentorEngagements',
  // `fallback` is only used when the key is missing; it exists in both locales.
  fallback: 'Mentor Engagements',
};

const SIDEBAR_RECENTS_CACHE_MS = 30_000;

let sidebarRecentDocumentsCache:
  | {
      items: UserDocumentItem[];
      fetchedAt: number;
    }
  | null = null;

let sidebarRecentDocumentsInFlight: Promise<UserDocumentItem[]> | null = null;
let sidebarRecentDocumentsGeneration = 0;

export function invalidateSidebarRecentDocumentsCache(): void {
  sidebarRecentDocumentsGeneration += 1;
  sidebarRecentDocumentsCache = null;
  sidebarRecentDocumentsInFlight = null;
}

function getCachedSidebarRecentDocuments(): UserDocumentItem[] | null {
  if (!sidebarRecentDocumentsCache) return null;
  if (Date.now() - sidebarRecentDocumentsCache.fetchedAt > SIDEBAR_RECENTS_CACHE_MS) {
    return null;
  }
  return sidebarRecentDocumentsCache.items;
}

async function loadSidebarRecentDocuments(): Promise<UserDocumentItem[]> {
  const cached = getCachedSidebarRecentDocuments();
  if (cached) return cached;
  if (sidebarRecentDocumentsInFlight) return sidebarRecentDocumentsInFlight;

  const requestGeneration = sidebarRecentDocumentsGeneration;
  sidebarRecentDocumentsInFlight = (async () => {
    try {
      const response = await userDocumentsAPI.list(0, 4);
      return response.documents || [];
    } catch (error) {
      console.warn('Failed to load sidebar recent documents:', error);
      const transcriptResponse = await transcriptionAPI.list(0, 4);
      return (transcriptResponse.transcripts || []).map((item): UserDocumentItem => ({
        id: item.id,
        document_type: 'user_transcript',
        original_filename: item.original_filename,
        file_size: 0,
        content_type: 'audio/*',
        created_at: item.created_at,
        audio_duration_seconds: item.audio_duration_seconds,
        transcript_language: item.transcript_language,
        transcription_status: item.transcription_status,
        grammar_correction_applied: item.grammar_correction_applied,
      }));
    }
  })()
    .then((items) => {
      if (requestGeneration === sidebarRecentDocumentsGeneration) {
        sidebarRecentDocumentsCache = {
          items,
          fetchedAt: Date.now(),
        };
      }
      return items;
    })
    .finally(() => {
      if (requestGeneration === sidebarRecentDocumentsGeneration) {
        sidebarRecentDocumentsInFlight = null;
      }
    });

  return sidebarRecentDocumentsInFlight;
}

function Brand({ compact = false, narrow = false }: { compact?: boolean; narrow?: boolean }) {
  return (
    <View style={[styles.brand, compact && styles.brandCompact, narrow && styles.brandNarrow]}>
      <Image source={AppLogo} style={styles.logo as any} resizeMode="contain" />
      {!compact && (
        <Text
          testID="shell-brand-name"
          style={styles.brandText}
          // The ui Text's `text-base` class beats a StyleSheet fontSize on web,
          // so the narrow size goes in as a class (tailwind-merge swaps it in).
          className={narrow ? 'text-[14px] leading-5' : undefined}
        >
          Mentor Coaching AI
        </Text>
      )}
    </View>
  );
}

type AnimatedNavItemProps = {
  testID: string;
  icon: keyof typeof MaterialIcons.glyphMap;
  label: string;
  isActive: boolean;
  onPress: () => void;
};

function AnimatedNavItem({ testID, icon, label, isActive, onPress }: AnimatedNavItemProps) {
  const focusV = useSharedValue(isActive ? 1 : 0);
  const hoverV = useSharedValue(0);
  const pressV = useSharedValue(0);

  React.useEffect(() => {
    focusV.value = withSpring(isActive ? 1 : 0, NAV_SPRING);
  }, [focusV, isActive]);

  const containerStyle = useAnimatedStyle(() => {
    const scale = 1 - pressV.value * 0.04;
    const translateX = hoverV.value * 3 * (1 - focusV.value);
    return {
      transform: [{ translateX } as any, { scale } as any],
    };
  });

  const pillStyle = useAnimatedStyle(() => {
    const focus = focusV.value;
    const hover = hoverV.value * (1 - focus);
    return {
      opacity: focus * 1 + hover * 0.55,
      transform: [{ scaleX: focus + hover * 0.96 }],
    };
  });

  const indicatorStyle = useAnimatedStyle(() => ({
    opacity: focusV.value,
    transform: [{ scaleY: 0.4 + focusV.value * 0.6 }],
  }));

  const iconStyle = useAnimatedStyle(() => ({
    transform: [{ scale: 1 + focusV.value * 0.08 - pressV.value * 0.04 }],
  }));

  const labelStyle = useAnimatedStyle(() => ({
    opacity: 0.78 + focusV.value * 0.22 + hoverV.value * 0.1 * (1 - focusV.value),
  }));

  const iconColor = isActive ? reviewDesk.accent : reviewDesk.inkSoft;
  const iconSize = 19;

  return (
    <Pressable
      testID={testID}
      onPress={onPress}
      onHoverIn={() => {
        hoverV.value = withTiming(1, NAV_FADE_TIMING);
      }}
      onHoverOut={() => {
        hoverV.value = withTiming(0, NAV_FADE_TIMING);
      }}
      onPressIn={() => {
        pressV.value = withSpring(1, NAV_PRESS_SPRING);
      }}
      onPressOut={() => {
        pressV.value = withSpring(0, NAV_PRESS_SPRING);
      }}
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityState={{ selected: isActive }}
      style={styles.sidebarNavItemPressable}
    >
      <Animated.View
        pointerEvents="none"
        style={[styles.sidebarNavPill, pillStyle]}
      />
      <Animated.View
        pointerEvents="none"
        style={[styles.sidebarNavIndicator, indicatorStyle]}
      />
      <Animated.View style={[styles.sidebarNavRow, containerStyle]}>
        <Animated.View style={iconStyle}>
          <MaterialIcons name={icon} size={iconSize} color={iconColor} />
        </Animated.View>
        <Animated.Text
          numberOfLines={1}
          style={[
            styles.sidebarNavText,
            isActive && styles.navTextActive,
            labelStyle,
          ]}
        >
          {label}
        </Animated.Text>
      </Animated.View>
    </Pressable>
  );
}

type PhoneNavItemProps = {
  testID: string;
  icon: keyof typeof MaterialIcons.glyphMap;
  label: string;
  isActive: boolean;
  onPress: () => void;
};

function PhoneNavItem({ testID, icon, label, isActive, onPress }: PhoneNavItemProps) {
  const color = isActive ? reviewDesk.accent : reviewDesk.inkSoft;
  return (
    <Pressable
      testID={testID}
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={label}
      accessibilityState={{ selected: isActive }}
      style={styles.phoneBarItem}
    >
      <View style={[styles.phoneBarIconPill, isActive && styles.phoneBarIconPillActive]}>
        <MaterialIcons name={icon} size={22} color={color} />
      </View>
      <Text style={[styles.phoneBarLabel, isActive && styles.navTextActive]} numberOfLines={1}>
        {label}
      </Text>
    </Pressable>
  );
}

export function WebAppShell({
  active,
  children,
  topRight,
  recentItems = [],
  contentMaxWidth = '100%',
  scroll = true,
}: WebAppShellProps) {
  const router = useRouter();
  const { t } = useTranslation();
  const {
    showDocuments,
    showPlatformAdmin,
    showSchoolManagement,
    showStudents,
    showProgress,
    isSchool,
    planTier,
    isMentorOrAdmin,
  } = useTabAccess();
  const { access: mentorAccess } = useMentorTrainingAccess();
  // Mentor engagements are a Tier 3 surface (D12); B2B mentors/admins get it too.
  const showMentorEngagements = planTier === 'tier3' || Boolean(isMentorOrAdmin);

  const navItems: ShellNavItem[] = React.useMemo(() => {
    const visibleBaseItems = baseNavItems.filter((item) => item.key !== 'progress' || showProgress);
    const extras: ShellNavItem[] = [];
    if (mentorAccess.has_access) extras.push(mentorTrainingNavItem);
    if (showMentorEngagements) {
      const mentorItems: ShellNavItem[] = [mentorEngagementsNavItem];
      if (MCS_ENABLED) mentorItems.push({ key: 'mcs-preparation', icon: 'checklist', route: ROUTES.MCS_PREPARATION, labelKey: 'mcs.title', fallback: 'MCS preparation' });
      // Home is always the first destination; the mentor surfaces follow it.
      visibleBaseItems.splice(1, 0, ...mentorItems);
    }
    if (extras.length === 0) return visibleBaseItems;
    // Insert the mentor surfaces before profile.
    const insertAt = visibleBaseItems.findIndex((item) => item.key === 'profile');
    if (insertAt < 0) return [...visibleBaseItems, ...extras];
    const next = [...visibleBaseItems];
    next.splice(insertAt, 0, ...extras);
    return next;
  }, [mentorAccess.has_access, showMentorEngagements, showProgress]);
  const { isPhone, isCompact: isBelowDesktop, width: viewportWidth } = useResponsive();
  // Phones (<760): compact header + labelled bottom bar + "More" sheet (D7).
  // Tablet/desktop web: sidebar. The stacked top header only remains for
  // native tablets (web-only product, D1).
  const useSidebar = Platform.OS === 'web' && !isPhone;
  const isNarrowSidebar = isBelowDesktop;
  const isStackedTop = !useSidebar && isBelowDesktop;
  const [moreOpen, setMoreOpen] = React.useState(false);
  // Help menu (QA F4): replay the guided tour, FAQ, feedback.
  const [helpOpen, setHelpOpen] = React.useState(false);
  // ADM-12: on phones the feedback entry lives in the header (FeedbackFab
  // hides its floating button there); shown only while the sheet is mounted.
  const feedbackAvailable = React.useSyncExternalStore(
    subscribeFeedbackAvailability,
    isFeedbackAvailable,
    isFeedbackAvailable,
  );
  const showHeaderFeedback = feedbackAvailable && usesHeaderFeedbackEntry(Platform.OS, viewportWidth);
  const shouldScroll = scroll;
  const shellStyle = Platform.OS === 'web' ? styles.webViewport : null;
  const scrollerStyle = Platform.OS === 'web' ? [styles.contentScroller, styles.webContentScroller] : styles.contentScroller;
  const [shellRecentDocuments, setShellRecentDocuments] = React.useState<UserDocumentItem[]>(() => (
    getCachedSidebarRecentDocuments() || []
  ));

  const adminNavCandidates: ShellNavItem[] = [
    {
      key: 'platform-admin',
      icon: 'admin-panel-settings',
      route: ROUTES.TABS_MORE_PLATFORM_ADMIN,
      labelKey: 'navigation.platformAdmin',
      fallback: 'Platform yönetimi',
    },
    {
      key: 'school-management',
      icon: 'school',
      route: ROUTES.TABS_MORE_SCHOOL_MANAGEMENT,
      labelKey: isSchool ? 'navigation.schoolAdmin' : 'navigation.schoolManagement',
      fallback: isSchool ? 'Okul yöneticisi' : 'Okul yönetimi',
    },
    {
      key: 'students',
      icon: 'groups',
      route: ROUTES.TABS_STUDENTS,
      labelKey: 'navigation.students',
      fallback: 'Üyeler',
    },
    {
      key: 'documents',
      icon: 'description',
      route: ROUTES.TABS_DOCUMENTS,
      labelKey: 'navigation.documents',
      fallback: 'Belgeler',
    },
  ];

  const adminNavItems = adminNavCandidates.filter((item) => {
    if (item.key === 'platform-admin') return showPlatformAdmin;
    if (item.key === 'school-management') return showSchoolManagement;
    if (item.key === 'students') return showStudents;
    if (item.key === 'documents') return showDocuments;
    return false;
  });

  const shellDocumentRecentItems = React.useMemo<WebAppShellRecentItem[]>(() => (
    shellRecentDocuments.map((item) => ({
      id: `shell-document-${item.id}`,
      title: item.original_filename,
      subtitle: item.document_type === 'user_transcript'
        ? t('library.typeVoice')
        : item.document_type === 'user_material'
          ? t('library.filterMaterials')
          : t('library.typeText'),
      onPress: () => {
        if (item.session_id) {
          router.push({
            pathname: ROUTES.TABS_CHAT,
            params: { sessionId: String(item.session_id) },
          } as any);
          return;
        }
        router.push(ROUTES.TABS_LIBRARY as any);
      },
    }))
  ), [router, shellRecentDocuments, t]);

  React.useEffect(() => {
    if (!useSidebar) {
      return;
    }

    let cancelled = false;
    const cached = getCachedSidebarRecentDocuments();
    if (cached) {
      setShellRecentDocuments(cached);
      return;
    }

    const loadFallbackRecents = async () => {
      try {
        const items = await loadSidebarRecentDocuments();
        if (!cancelled) setShellRecentDocuments(items);
      } catch (error) {
        console.warn('Failed to load sidebar recents:', error);
        if (!cancelled) setShellRecentDocuments([]);
      }
    };

    loadFallbackRecents();

    return () => {
      cancelled = true;
    };
  }, [useSidebar]);

  const sidebarRecentItems = shellDocumentRecentItems.length > 0 ? shellDocumentRecentItems : recentItems;

  const renderNavItem = (item: ShellNavItem) => {
    const isActive = active === item.key;
    return (
      <AnimatedNavItem
        key={item.key}
        testID={`shell-nav-${item.key}`}
        icon={item.icon}
        label={t(item.labelKey, item.fallback)}
        isActive={isActive}
        onPress={() => router.push(item.route as any)}
      />
    );
  };

  // The sidebar stacks items vertically; the native top header lays the same
  // items out as a (wrapping) row.
  const navContainerStyle = useSidebar
    ? styles.sidebarNav
    : isStackedTop
      ? styles.topNavWrap
      : styles.topNav;
  const nav = (
    <View style={navContainerStyle} testID={useSidebar ? 'shell-sidebar-nav' : undefined}>
      {navItems.map(renderNavItem)}
      {adminNavItems.length > 0 && (
        <>
          <Text style={styles.sidebarSectionLabel}>
            {t('navigation.adminSection')}
          </Text>
          {adminNavItems.map(renderNavItem)}
        </>
      )}
    </View>
  );

  const contentStyle = [
    styles.contentInner,
    isNarrowSidebar && styles.contentInnerNarrow,
    isPhone && styles.contentInnerPhone,
    contentMaxWidth !== '100%' ? { maxWidth: contentMaxWidth as number } : styles.contentInnerFluid,
  ];

  const animatedChildren = (
    <Animated.View
      key={`screen-${active}`}
      entering={FadeInDown.duration(280).easing(Easing.out(Easing.cubic))}
      style={styles.contentAnimatedWrapper}
    >
      {/* A crash in the screen body keeps the shell (and its navigation)
          usable; "Try again" remounts just the screen content (FE-22). */}
      <ErrorBoundary scope="screen" resetKey={active}>
        {children}
      </ErrorBoundary>
    </Animated.View>
  );

  const content = shouldScroll ? (
    <ScrollView
      style={scrollerStyle}
      contentContainerStyle={contentStyle}
      showsVerticalScrollIndicator
    >
      {animatedChildren}
    </ScrollView>
  ) : (
    <View style={[contentStyle, styles.contentNoScroll]}>{animatedChildren}</View>
  );

  if (useSidebar) {
    return (
      <View style={[styles.sidebarLayout, shellStyle]}>
        <View style={[styles.sidebar, isNarrowSidebar && styles.sidebarNarrow]}>
          <Brand narrow={isNarrowSidebar} />
          {nav}
          <View style={styles.sidebarHelp}>
            <AnimatedNavItem
              testID="shell-help"
              icon="help-outline"
              label={t('navigation.help')}
              isActive={helpOpen}
              onPress={() => setHelpOpen(true)}
            />
          </View>
          <View style={styles.sidebarDivider} />
          <Text style={styles.recentTitle}>{t('dashboard.recentSessionsTitle')}</Text>
          {sidebarRecentItems.length > 0 ? (
            <View style={styles.recentList}>
              {sidebarRecentItems.map((item, idx) => (
                <Animated.View
                  key={String(item.id)}
                  entering={FadeInLeft.delay(60 * idx)
                    .duration(260)
                    .easing(Easing.out(Easing.cubic))}
                >
                  <TouchableOpacity
                    style={[styles.recentItem, item.active && styles.recentItemActive]}
                    onPress={item.onPress}
                    accessibilityRole={item.onPress ? 'button' : undefined}
                  >
                    <Text style={styles.recentItemTitle} numberOfLines={1}>{item.title}</Text>
                    {!!item.subtitle && (
                      <Text style={styles.recentItemSubtitle} numberOfLines={1}>{item.subtitle}</Text>
                    )}
                  </TouchableOpacity>
                </Animated.View>
              ))}
            </View>
          ) : (
            <Animated.Text
              entering={FadeIn.duration(220)}
              style={styles.recentEmpty}
            >
              {t('dashboard.noRecentSessions')}
            </Animated.Text>
          )}
          <View style={styles.sidebarPrivacy}>
            <MaterialIcons name="verified-user" size={22} color={reviewDesk.accent} />
            <Text style={styles.privacyTitle}>{t('common.secure')}</Text>
            <Text style={styles.privacyCopy}>
              {t('dashboard.webPrivacyNote')}
            </Text>
          </View>
        </View>
        <View style={styles.sidebarMain}>
          {!!topRight && (
            <View style={styles.sidebarToolbar}>
              {topRight}
            </View>
          )}
          {content}
        </View>
        <HelpMenu visible={helpOpen} onClose={() => setHelpOpen(false)} />
      </View>
    );
  }

  if (isPhone) {
    const phoneNav = splitPhoneNav(navItems, adminNavItems);
    const moreIsActive = [...phoneNav.more, ...phoneNav.moreAdmin].some((item) => item.key === active);
    const goTo = (item: ShellNavItem) => {
      setMoreOpen(false);
      router.push(item.route as any);
    };
    const profileLabel = t('navigation.profile');
    const renderMoreRow = (item: ShellNavItem) => {
      const isActive = active === item.key;
      return (
        <Pressable
          key={item.key}
          testID={`shell-more-${item.key}`}
          onPress={() => goTo(item)}
          accessibilityRole="button"
          accessibilityState={{ selected: isActive }}
          style={[styles.moreRow, isActive && styles.moreRowActive]}
        >
          <MaterialIcons name={item.icon} size={22} color={isActive ? reviewDesk.accent : reviewDesk.inkSoft} />
          <Text style={[styles.moreRowText, isActive && styles.navTextActive]} numberOfLines={1}>
            {t(item.labelKey, item.fallback)}
          </Text>
          <MaterialIcons name="chevron-right" size={20} color={reviewDesk.muted} />
        </Pressable>
      );
    };

    return (
      <View style={[styles.topLayout, shellStyle]}>
        <View style={styles.phoneHeader} testID="shell-phone-header">
          <Brand compact />
          <View style={styles.phoneHeaderActions}>
            {showHeaderFeedback && (
              <Pressable
                testID="shell-feedback"
                onPress={() => openFeedback()}
                accessibilityRole="button"
                accessibilityLabel={t('feedback.button')}
                style={styles.phoneAccountButton}
              >
                <MaterialIcons name="feedback" size={24} color={reviewDesk.inkSoft} />
              </Pressable>
            )}
            <Pressable
              testID="shell-help"
              onPress={() => setHelpOpen(true)}
              accessibilityRole="button"
              accessibilityLabel={t('navigation.help')}
              style={[styles.phoneAccountButton, helpOpen && styles.phoneAccountButtonActive]}
            >
              <MaterialIcons name="help-outline" size={26} color={helpOpen ? reviewDesk.accent : reviewDesk.inkSoft} />
            </Pressable>
            <Pressable
              testID="shell-nav-profile"
              onPress={() => router.push(ROUTES.PROFILE as any)}
              accessibilityRole="button"
              accessibilityLabel={profileLabel}
              accessibilityState={{ selected: active === 'profile' }}
              style={[styles.phoneAccountButton, active === 'profile' && styles.phoneAccountButtonActive]}
            >
              <MaterialIcons
                name="account-circle"
                size={28}
                color={active === 'profile' ? reviewDesk.accent : reviewDesk.inkSoft}
              />
            </Pressable>
          </View>
        </View>
        {!!topRight && (
          // Screen actions get their own row so the header stays 56px.
          <View style={styles.phoneToolbar} testID="shell-phone-toolbar">{topRight}</View>
        )}
        {content}
        <View style={styles.phoneBar} testID="shell-phone-bar">
          {phoneNav.bar.map((item) => (
            <PhoneNavItem
              key={item.key}
              testID={`shell-nav-${item.key}`}
              icon={item.icon}
              label={t(item.labelKey, item.fallback)}
              isActive={active === item.key}
              onPress={() => goTo(item)}
            />
          ))}
          {phoneNav.more.length + phoneNav.moreAdmin.length > 0 && (
            <PhoneNavItem
              testID="shell-nav-more"
              icon="menu"
              label={t('navigation.more')}
              isActive={moreIsActive || moreOpen}
              onPress={() => setMoreOpen(true)}
            />
          )}
        </View>
        <Sheet
          visible={moreOpen}
          onClose={() => setMoreOpen(false)}
          title={t('navigation.more')}
          closeLabel={t('common.close')}
          testID="shell-more-sheet"
        >
          {phoneNav.more.map(renderMoreRow)}
          {phoneNav.moreAdmin.length > 0 && (
            <>
              <Text style={styles.moreSectionLabel}>{t('navigation.adminSection')}</Text>
              {phoneNav.moreAdmin.map(renderMoreRow)}
            </>
          )}
        </Sheet>
        <HelpMenu visible={helpOpen} onClose={() => setHelpOpen(false)} />
      </View>
    );
  }

  return (
    <View style={[styles.topLayout, shellStyle]}>
      <View style={[styles.topHeader, isStackedTop && styles.topHeaderStacked]}>
        {isStackedTop ? (
          <>
            <View style={styles.topHeaderBar}>
              <Brand />
              {!!topRight && <View style={styles.topRight}>{topRight}</View>}
            </View>
            {nav}
          </>
        ) : (
          <>
            <Brand />
            {nav}
            {!!topRight && <View style={styles.topRight}>{topRight}</View>}
          </>
        )}
      </View>
      {content}
    </View>
  );
}

const styles = StyleSheet.create({
  // Bounded column (FE-13): the header keeps its height and the content
  // ScrollView below takes the rest, so it is the element that scrolls. A
  // content-sized layout here grows past the viewport and gets clipped by
  // react-navigation's overflow:hidden screen container / body{overflow:hidden}.
  topLayout: {
    flex: 1,
    minHeight: 0,
    backgroundColor: reviewDesk.canvas,
  },
  topHeader: {
    flexShrink: 0,
    width: '100%',
    minHeight: 94,
    paddingHorizontal: 24,
    paddingVertical: 18,
    borderBottomWidth: 1,
    borderBottomColor: reviewDesk.line,
    backgroundColor: reviewDesk.card,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 22,
  },
  topHeaderStacked: {
    alignItems: 'flex-start',
    flexDirection: 'column',
    gap: 14,
  },
  brand: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flexShrink: 0,
  },
  brandCompact: {
    justifyContent: 'center',
  },
  // Phone shell (<760, D7): 56px header, toolbar row for screen actions,
  // labelled bottom bar.
  phoneHeader: {
    flexShrink: 0,
    height: 56,
    paddingHorizontal: 12,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    borderBottomWidth: 1,
    borderBottomColor: reviewDesk.line,
    backgroundColor: reviewDesk.card,
    ...(Platform.OS === 'web' ? ({ paddingTop: 'env(safe-area-inset-top, 0px)', boxSizing: 'content-box' } as any) : null),
  },
  phoneHeaderActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
  },
  phoneAccountButton: {
    width: 44,
    height: 44,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
  },
  phoneAccountButtonActive: {
    backgroundColor: reviewDesk.accentSoft,
  },
  phoneToolbar: {
    flexShrink: 0,
    paddingHorizontal: 14,
    paddingVertical: 8,
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 8,
    borderBottomWidth: 1,
    borderBottomColor: reviewDesk.line,
    backgroundColor: reviewDesk.canvas,
  },
  phoneBar: {
    flexShrink: 0,
    flexDirection: 'row',
    alignItems: 'stretch',
    borderTopWidth: 1,
    borderTopColor: reviewDesk.line,
    backgroundColor: reviewDesk.card,
    paddingHorizontal: 4,
    ...(Platform.OS === 'web' ? ({ paddingBottom: 'env(safe-area-inset-bottom, 0px)' } as any) : null),
  },
  phoneBarItem: {
    flex: 1,
    minWidth: 0,
    minHeight: 56,
    paddingVertical: 6,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 2,
    ...(Platform.OS === 'web' ? ({ cursor: 'pointer' } as any) : null),
  },
  phoneBarIconPill: {
    width: 48,
    height: 28,
    borderRadius: 999,
    alignItems: 'center',
    justifyContent: 'center',
  },
  phoneBarIconPillActive: {
    backgroundColor: reviewDesk.accentSoft,
  },
  phoneBarLabel: {
    color: reviewDesk.inkSoft,
    fontSize: 11,
    fontWeight: '700',
  },
  moreRow: {
    minHeight: 52,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 14,
    paddingHorizontal: 12,
    borderRadius: 12,
  },
  moreRowActive: {
    backgroundColor: reviewDesk.accentSoft,
  },
  moreRowText: {
    flex: 1,
    color: reviewDesk.ink,
    fontSize: 16,
    fontWeight: '700',
  },
  moreSectionLabel: {
    color: reviewDesk.muted,
    fontSize: 11,
    fontWeight: '800',
    marginTop: 14,
    marginBottom: 4,
    paddingHorizontal: 12,
    textTransform: 'uppercase',
  },
  logo: {
    width: 42,
    height: 42,
    borderRadius: 10,
  },
  brandText: {
    color: reviewDesk.ink,
    fontSize: 19,
    fontWeight: '800',
  },
  // The 236px tablet sidebar leaves ~146px beside the logo, so the name broke
  // onto two lines (founder QA 2026-10-16); with 14px type and a tighter gap
  // it stays on one.
  brandNarrow: {
    gap: 10,
  },
  topNav: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flex: 1,
    flexWrap: 'nowrap',
  },
  topNavWrap: {
    width: '100%',
    flexGrow: 0,
    flexShrink: 0,
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
  },
  topHeaderBar: {
    width: '100%',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
  },
  topNavItem: {
    minHeight: 42,
    borderRadius: 10,
    paddingHorizontal: 13,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    flexShrink: 0,
  },
  topNavItemActive: {
    backgroundColor: reviewDesk.accentSoft,
  },
  topNavText: {
    color: reviewDesk.inkSoft,
    fontSize: 16,
    fontWeight: '700',
  },
  navTextActive: {
    color: reviewDesk.accent,
  },
  topRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    marginLeft: 'auto',
  },
  contentScroller: {
    flex: 1,
    minHeight: 0,
  },
  webContentScroller: {
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
    overscrollBehavior: 'contain',
  } as any,
  contentInner: {
    width: '100%',
    flexGrow: 1,
    alignSelf: 'center',
    padding: 24,
  },
  contentInnerNarrow: {
    padding: 20,
  },
  contentInnerPhone: {
    padding: 14,
  },
  contentNoScroll: {
    flex: 1,
    minHeight: 0,
  },
  contentAnimatedWrapper: {
    flex: 1,
    minHeight: 0,
  },
  contentInnerFluid: {
    maxWidth: '100%',
  },
  sidebarLayout: {
    minHeight: '100%',
    flexDirection: 'row',
    backgroundColor: reviewDesk.canvas,
  },
  webViewport: {
    height: '100dvh',
    minHeight: 0,
    overflow: 'hidden',
  } as any,
  sidebar: {
    width: 272,
    minHeight: '100%',
    padding: 24,
    borderRightWidth: 1,
    borderRightColor: reviewDesk.line,
    backgroundColor: reviewDesk.card,
  },
  sidebarNarrow: {
    width: 236,
    paddingHorizontal: 18,
  },
  sidebarMain: {
    flex: 1,
    minWidth: 0,
    minHeight: 0,
    overflow: 'hidden',
  },
  sidebarToolbar: {
    minHeight: 72,
    flexShrink: 0,
    paddingHorizontal: 28,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: reviewDesk.line,
    backgroundColor: reviewDesk.canvas,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    gap: 10,
  },
  sidebarNav: {
    marginTop: 28,
    gap: 6,
    width: '100%',
  },
  sidebarNavItemPressable: {
    position: 'relative',
    minHeight: 44,
    borderRadius: 10,
    overflow: 'hidden',
    alignSelf: 'stretch',
    ...(Platform.OS === 'web' ? ({ cursor: 'pointer', transitionProperty: 'none' } as any) : null),
  },
  sidebarNavRow: {
    minHeight: 44,
    paddingHorizontal: 14,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  sidebarNavPill: {
    position: 'absolute',
    top: 4,
    bottom: 4,
    left: 4,
    right: 4,
    borderRadius: 10,
    backgroundColor: reviewDesk.accentSoft,
    transformOrigin: 'left center' as any,
  },
  sidebarNavIndicator: {
    position: 'absolute',
    left: 0,
    top: 8,
    bottom: 8,
    width: 3,
    borderRadius: 999,
    backgroundColor: reviewDesk.accent,
  },
  sidebarSectionLabel: {
    color: reviewDesk.muted,
    fontSize: 11,
    fontWeight: '800',
    marginTop: 18,
    marginBottom: 2,
    paddingHorizontal: 14,
    textTransform: 'uppercase',
  },
  sidebarNavText: {
    color: reviewDesk.inkSoft,
    fontSize: 15,
    fontWeight: '700',
  },
  sidebarHelp: {
    marginTop: 6,
    width: '100%',
  },
  sidebarDivider: {
    height: 1,
    backgroundColor: reviewDesk.line,
    marginVertical: 28,
  },
  recentTitle: {
    color: reviewDesk.ink,
    fontSize: 13,
    fontWeight: '800',
    marginBottom: 10,
  },
  recentList: {
    gap: 8,
  },
  recentItem: {
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'transparent',
    paddingVertical: 9,
    paddingHorizontal: 10,
  },
  recentItemActive: {
    borderColor: reviewDesk.line,
    backgroundColor: reviewDesk.accentWash,
  },
  recentItemTitle: {
    color: reviewDesk.ink,
    fontSize: 13,
    fontWeight: '700',
  },
  recentItemSubtitle: {
    color: reviewDesk.muted,
    fontSize: 12,
    marginTop: 3,
  },
  recentEmpty: {
    color: reviewDesk.muted,
    fontSize: 13,
  },
  sidebarPrivacy: {
    marginTop: 'auto',
    borderWidth: 1,
    borderColor: reviewDesk.line,
    borderRadius: 10,
    padding: 14,
    backgroundColor: reviewDesk.amberSoft,
    gap: 6,
  },
  privacyTitle: {
    color: reviewDesk.ink,
    fontSize: 13,
    fontWeight: '800',
  },
  privacyCopy: {
    color: reviewDesk.inkSoft,
    fontSize: 12,
    lineHeight: 17,
  },
});
